import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronUpIcon, ArrowLeftIcon, PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import ImageLightbox from '@/shared/ImageLightbox'
import Modal from '@/shared/Modal'
import Card from '@/shared/Card'
import TipTapWysiwyg from '@/shared/TipTapWysiwyg'
import { useGlobalState } from '@/state'
import FeedbackImageUploader from '@/pages/Feedback/FeedbackImageUploader'

const CATEGORIES = [
  { key: 'feature_request', label: 'Feature Request' },
  { key: 'bug_report', label: 'Bug Report' }
]

const STATUS_COLORS = {
  under_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  planned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  maybe: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  complete: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  closed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
}

const CATEGORY_COLORS = {
  feature_request: 'bg-cccpurple/20 text-cccpurple dark:bg-purple-900/40 dark:text-purple-200',
  bug_report: 'bg-cccorange/20 text-cccorange dark:bg-orange-900/40 dark:text-orange-200'
}

const CommentRow = ({ comment, currentUser, onDelete, onDeleteImage, onUpdate }) => {
  const [, setLightbox] = useGlobalState('lightbox')
  const canModifyComment = currentUser?.id === comment.user.id
  const canDeleteImages = currentUser?.id === comment.user.id
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editBody, setEditBody] = useState(comment.body || '')
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState(null)

  const startEdit = () => {
    setEditBody(comment.body || '')
    setEditError(null)
    setConfirmDelete(false)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setEditError(null)
  }

  const saveEdit = () => {
    if (!editBody.trim()) { setEditError('Comment cannot be empty'); return }
    setSaving(true)
    onUpdate(comment.id, editBody, (err) => {
      setSaving(false)
      if (err) { setEditError(typeof err === 'string' ? err : 'Failed to save'); return }
      setEditing(false)
      setEditError(null)
    })
  }

  return (
    <div className='flex gap-3 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0'>
      <a href={`/m/${comment.user.username}`} className='flex-shrink-0'>
        <img className='h-8 w-8 rounded-sm' src={comment.user.imageUrl} alt={comment.user.displayName} />
      </a>
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2 mb-1'>
          <a href={`/m/${comment.user.username}`} className='text-sm font-medium text-gray-900 dark:text-white hover:text-cccblue'>
            {comment.user.displayName}
          </a>
          <span className='text-xs text-gray-400'>{comment.createdAt}</span>
          {canModifyComment && !editing && (
            <div className='ml-auto flex items-center gap-2'>
              <button
                type='button'
                onClick={startEdit}
                className='text-xs text-gray-400 hover:text-cccblue'
                title='Edit comment'
              >
                <PencilIcon className='h-3.5 w-3.5' />
              </button>
              <button
                type='button'
                onClick={() => (confirmDelete ? onDelete(comment.id) : setConfirmDelete(true))}
                className={`text-xs ${confirmDelete ? 'text-red-600 font-semibold' : 'text-gray-400 hover:text-red-500'}`}
                title='Delete comment'
              >
                {confirmDelete ? 'Really delete?' : <TrashIcon className='h-3.5 w-3.5' />}
              </button>
            </div>
          )}
        </div>

        {!editing && (
          <div
            className='prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 changelog-body'
            dangerouslySetInnerHTML={{ __html: comment.body }}
          />
        )}

        {editing && (
          <div>
            <TipTapWysiwyg
              html={editBody}
              updateHtml={setEditBody}
              placeholder='Edit comment...'
              onSubmitShortcut={saveEdit}
            />
            {editError && <p className='mt-1 text-sm text-red-600'>{editError}</p>}
            <div className='flex justify-end gap-2 mt-2'>
              <button
                type='button'
                onClick={cancelEdit}
                disabled={saving}
                className='px-3 py-1.5 text-xs font-medium rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={saveEdit}
                disabled={saving || !editBody.trim()}
                className='px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt disabled:opacity-50'
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {comment.images && comment.images.length > 0 && (
          <div className='flex gap-2 mt-2 flex-wrap'>
            {comment.images.map((img) => (
              <div key={img.id} className='relative group'>
                <button type='button' onClick={() => setLightbox(img.url)}>
                  <img className='h-20 w-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700' src={img.url} alt='' />
                </button>
                {canDeleteImages && (
                  <button
                    type='button'
                    onClick={() => onDeleteImage(comment.id, img.id)}
                    className='absolute -top-1 -right-1 bg-white dark:bg-gray-800 rounded-full shadow opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity'
                    aria-label='Remove image'
                  >
                    <XCircleIcon className='h-5 w-5 text-red-500' />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const FeedbackShow = () => {
  const { slug } = useParams()
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const [, setLightbox] = useGlobalState('lightbox')
  const { getRequest, putpostRequest } = useQuery()
  const [feedback, setFeedback] = useState(null)
  const [comments, setComments] = useState([])
  const [voted, setVoted] = useState(false)
  const [votesCount, setVotesCount] = useState(0)
  const [commentBody, setCommentBody] = useState('')
  const [commentImageUrls, setCommentImageUrls] = useState([])
  // TipTap reads `content` only at creation; bumping `idx` triggers its
  // clearContent effect so the editor visually empties on submit.
  const [commentResetIdx, setCommentResetIdx] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editCategory, setEditCategory] = useState('feature_request')
  const [editImageUrls, setEditImageUrls] = useState([])
  const [saving, setSaving] = useState(false)
  const [editErrors, setEditErrors] = useState(null)
  const [upvotersOpen, setUpvotersOpen] = useState(false)
  const [allUpvoters, setAllUpvoters] = useState([])
  const [upvotersMeta, setUpvotersMeta] = useState({})
  const [upvotersLoading, setUpvotersLoading] = useState(false)

  useEffect(() => {
    getRequest(`/api/v3/feedbacks/${slug}`, {}, (err, jsonData) => {
      if (err) return
      setFeedback(jsonData.feedback)
      setVoted(jsonData.feedback.voted)
      setVotesCount(jsonData.feedback.votesCount)
    })
    getRequest(`/api/v3/feedbacks/${slug}/comments`, {}, (err, jsonData) => {
      if (err) return
      setComments(jsonData.comments)
    })
  }, [slug])

  const handleVote = () => {
    if (!currentUser?.id) { window.location.href = `/login?fwd=/feedback/${slug}`; return }

    setVoted(!voted)
    setVotesCount(voted ? votesCount - 1 : votesCount + 1)
    putpostRequest(`/api/v3/feedbacks/${slug}/vote`, 'POST', {}, (err, jsonData) => {
      if (err) { setVoted(voted); return }
      setVoted(jsonData.voted)
      setVotesCount(jsonData.votesCount)
    })
  }

  const submitComment = () => {
    if (!commentBody.trim() && commentImageUrls.length === 0) return

    setSubmitting(true)
    putpostRequest(`/api/v3/feedback_comments`, 'POST', {
      feedback_id: feedback.slug,
      feedback_comment: { body: commentBody },
      image_urls: commentImageUrls
    }, (err, jsonData) => {
      setSubmitting(false)
      if (err) return
      setComments([...comments, jsonData.comment])
      setCommentBody('')
      setCommentImageUrls([])
      setCommentResetIdx((i) => i + 1)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Comment added</p>
      </div>)
    })
  }

  const startEdit = () => {
    setEditTitle(feedback.title)
    setEditDescription(feedback.description || '')
    setEditCategory(feedback.category)
    setEditImageUrls((feedback.images || []).map((img) => img.url))
    setEditErrors(null)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setEditErrors(null)
  }

  const saveEdit = () => {
    if (!editTitle.trim()) { setEditErrors({ title: 'Title is required' }); return }

    setSaving(true)
    setEditErrors(null)
    putpostRequest(`/api/v3/feedbacks/${slug}`, 'PATCH', {
      feedback: { title: editTitle, description: editDescription, category: editCategory },
      image_urls: editImageUrls
    }, (err, jsonData) => {
      setSaving(false)
      if (err) { setEditErrors(typeof err === 'object' ? err : { base: err }); return }
      setFeedback(jsonData.feedback)
      setEditing(false)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Feedback updated</p>
      </div>)
    })
  }

  const fetchUpvoters = (page = 1) => {
    setUpvotersLoading(true)
    getRequest(`/api/v3/feedbacks/${slug}/upvoters`, { page }, (err, jsonData) => {
      setUpvotersLoading(false)
      if (err) return
      setAllUpvoters((prev) => (page === 1 ? jsonData.upvoters : [...prev, ...jsonData.upvoters]))
      setUpvotersMeta(jsonData.meta)
    })
  }

  const openUpvoters = () => {
    setAllUpvoters([])
    setUpvotersMeta({})
    setUpvotersOpen(true)
    fetchUpvoters(1)
  }

  const deleteComment = (commentId) => {
    putpostRequest(`/api/v3/feedback_comments/${commentId}`, 'DELETE', {}, (err) => {
      if (err) return
      setComments((prev) => prev.filter((c) => c.id !== commentId))
    })
  }

  const updateComment = (commentId, body, callback) => {
    putpostRequest(`/api/v3/feedback_comments/${commentId}`, 'PATCH', {
      feedback_comment: { body }
    }, (err, jsonData) => {
      if (err) { callback?.(err); return }
      setComments((prev) => prev.map((c) => (c.id === commentId ? jsonData.comment : c)))
      callback?.(null)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Comment updated</p>
      </div>)
    })
  }

  const deleteFeedbackImage = (imageId) => {
    putpostRequest(`/api/v3/feedback_images/${imageId}`, 'DELETE', {}, (err) => {
      if (err) return
      setFeedback({ ...feedback, images: (feedback.images || []).filter((i) => i.id !== imageId) })
    })
  }

  const deleteCommentImage = (commentId, imageId) => {
    putpostRequest(`/api/v3/feedback_images/${imageId}`, 'DELETE', {}, (err) => {
      if (err) return
      setComments((prev) => prev.map((c) => (
        c.id === commentId ? { ...c, images: (c.images || []).filter((i) => i.id !== imageId) } : c
      )))
    })
  }

  if (!feedback) return <Loading />

  if (feedback.mergedInto) {
    return (
      <div className='max-w-3xl mx-auto'>
        <Link to='/feedback' className='inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-cccblue mb-4'>
          <ArrowLeftIcon className='h-4 w-4' /> Back to Feedback
        </Link>
        <div className='rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 text-center'>
          <h1 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>This feedback was merged</h1>
          <p className='text-sm text-gray-600 dark:text-gray-300 mb-4'>
            "{feedback.title}" is now tracked as part of another feedback.
          </p>
          <Link
            to={`/feedback/${feedback.mergedInto.slug}`}
            className='inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt'
          >
            Go to "{feedback.mergedInto.title}"
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className='max-w-3xl mx-auto'>
      <div className='flex items-center justify-between mb-4'>
        <Link to='/feedback' className='inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-cccblue'>
          <ArrowLeftIcon className='h-4 w-4' /> Back to Feedback
        </Link>
        {currentUser?.isMod && (
          <a href={`/mods/feedback/${slug}`} className='inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600'>
            View in mod area
          </a>
        )}
      </div>

      <div className='rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-start gap-4'>
          {/* Vote */}
          <button
            onClick={handleVote}
            className={`flex flex-col items-center justify-center px-4 py-3 rounded-xl border transition-colors ${
              voted
                ? 'border-cccblue bg-cccblue/10 text-cccblue'
                : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-cccblue hover:text-cccblue'
            }`}
          >
            <ChevronUpIcon className='h-5 w-5' />
            <span className='text-lg font-bold'>{votesCount}</span>
            <span className='text-xs'>{voted ? 'Voted' : 'Vote'}</span>
          </button>

          <div className='flex-1 min-w-0'>
            <div className='flex flex-wrap items-center gap-2 mb-2'>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[feedback.category]}`}>
                {feedback.category === 'feature_request' ? 'Feature Request' : 'Bug Report'}
              </span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[feedback.status]}`}>
                {feedback.status.replace('_', ' ')}
              </span>
              {!editing && currentUser?.id === feedback.user.id && feedback.status === 'under_review' && (
                <button
                  onClick={startEdit}
                  className='ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-cccblue dark:text-gray-400'
                >
                  <PencilIcon className='h-3.5 w-3.5' /> Edit
                </button>
              )}
            </div>

            {!editing && (
              <>
                <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-3'>{feedback.title}</h1>

                <div className='flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4'>
                  <a href={`/m/${feedback.user.username}`} className='flex items-center gap-1 hover:text-cccblue'>
                    <img className='h-5 w-5 rounded-sm' src={feedback.user.imageUrl} alt='' />
                    {feedback.user.displayName}
                  </a>
                  <span>{feedback.createdAt}</span>
                </div>

                {feedback.description && (
                  <div
                    className='prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-200 changelog-body'
                    dangerouslySetInnerHTML={{ __html: feedback.description }}
                  />
                )}

                {feedback.upvoters && feedback.upvoters.length > 0 && (
                  <div className='flex items-center gap-3 mt-4'>
                    <div className='flex -space-x-2'>
                      {feedback.upvoters.slice(0, 5).map((u) => (
                        <a
                          key={u.id}
                          href={`/m/${u.username}`}
                          title={u.displayName}
                          className='inline-block h-7 w-7 rounded-full ring-2 ring-white dark:ring-gray-800 overflow-hidden hover:z-10 transition-transform hover:scale-110'
                        >
                          <img src={u.imageUrl} alt={u.displayName} className='h-full w-full object-cover' />
                        </a>
                      ))}
                    </div>
                    {votesCount > 5 && (
                      <button
                        type='button'
                        onClick={openUpvoters}
                        className='text-xs text-gray-500 dark:text-gray-400 hover:text-cccblue hover:underline'
                      >
                        +{votesCount - 5} more
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {editing && (
              <div className='space-y-3'>
                <div>
                  <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1'>Category</label>
                  <div className='flex gap-2'>
                    {CATEGORIES.map((c) => (
                      <button
                        key={c.key}
                        type='button'
                        onClick={() => setEditCategory(c.key)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          editCategory === c.key
                            ? 'bg-cccblue text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1'>Title</label>
                  <input
                    type='text'
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className={`block w-full ${editErrors?.title ? 'border-red-500' : ''}`}
                    maxLength={200}
                  />
                  {editErrors?.title && <p className='mt-1 text-sm text-red-600'>{editErrors.title}</p>}
                </div>

                <div>
                  <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1'>Description</label>
                  <TipTapWysiwyg
                    html={editDescription}
                    updateHtml={setEditDescription}
                    placeholder='Describe the feature or bug in detail...'
                  />
                </div>

                <div>
                  <label className='block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1'>Images</label>
                  <FeedbackImageUploader urls={editImageUrls} onChange={setEditImageUrls} />
                </div>

                {editErrors?.base && <p className='text-sm text-red-600'>{editErrors.base}</p>}

                <div className='flex justify-end gap-2 pt-2'>
                  <button
                    onClick={cancelEdit}
                    disabled={saving}
                    className='px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving || !editTitle.trim()}
                    className='px-4 py-2 text-sm font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt disabled:opacity-50 transition-colors'
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {!editing && feedback.images && feedback.images.length > 0 && (
              <div className='flex gap-2 mt-4 flex-wrap'>
                {feedback.images.map((img) => (
                  <div key={img.id} className='relative group'>
                    <button type='button' onClick={() => setLightbox(img.url)}>
                      <img className='h-32 w-auto object-cover rounded-lg border border-gray-200 dark:border-gray-700' src={img.url} alt='' />
                    </button>
                    {currentUser?.id === feedback.user.id && (
                      <button
                        type='button'
                        onClick={() => deleteFeedbackImage(img.id)}
                        className='absolute -top-1 -right-1 bg-white dark:bg-gray-800 rounded-full shadow opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity'
                        aria-label='Remove image'
                      >
                        <XCircleIcon className='h-5 w-5 text-red-500' />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Comments */}
      <div className='mt-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6'>
        <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
          Comments ({comments.length})
        </h2>

        {comments.length > 0 && (
          <div className='mb-6'>
            {comments.map((comment) => (
              <CommentRow
                key={comment.id}
                comment={comment}
                currentUser={currentUser}
                onDelete={deleteComment}
                onDeleteImage={deleteCommentImage}
                onUpdate={updateComment}
              />
            ))}
          </div>
        )}

        {currentUser?.id ? (
          <div>
            <TipTapWysiwyg
              html={commentBody}
              updateHtml={setCommentBody}
              placeholder='Add a comment...'
              idx={commentResetIdx}
              onSubmitShortcut={submitComment}
            />
            <div className='mt-2'>
              <FeedbackImageUploader urls={commentImageUrls} onChange={setCommentImageUrls} />
            </div>
            <div className='flex justify-end mt-2'>
              <button
                onClick={submitComment}
                disabled={submitting || (!commentBody.trim() && commentImageUrls.length === 0)}
                className='px-4 py-2 text-sm font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt disabled:opacity-50 transition-colors'
              >
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        ) : (
          <p className='text-sm text-gray-500 dark:text-gray-400'>
            <a href={`/login?fwd=/feedback/${slug}`} className='text-cccblue hover:underline'>Log in</a> to leave a comment.
          </p>
        )}
      </div>

      <Modal isOpen={upvotersOpen} closeModal={() => setUpvotersOpen(false)} title={`${votesCount} ${votesCount === 1 ? 'upvoter' : 'upvoters'}`} maxWidth='max-w-md'>
        <div className='mt-4 max-h-96 overflow-y-auto'>
          {allUpvoters.length === 0 && upvotersLoading && (
            <div className='py-8'><Loading /></div>
          )}
          {allUpvoters.length > 0 && (
            <ul className='divide-y divide-gray-200 dark:divide-slate-400/30'>
              {allUpvoters.map((u) => (
                <li key={u.id}>
                  <a
                    href={`/m/${u.username}`}
                    className='flex items-center gap-3 py-2 px-1 rounded hover:bg-black/5 dark:hover:bg-white/5'
                  >
                    <img src={u.imageUrl} alt={u.displayName} className='h-8 w-8 rounded-sm object-cover' />
                    <span className='text-sm font-medium text-slate-900 dark:text-white'>{u.displayName}</span>
                    <span className='text-xs text-slate-500 dark:text-slate-100 ml-auto'>@{u.username}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
          {upvotersMeta.nextPage && (
            <div className='flex justify-center pt-3'>
              <button
                type='button'
                onClick={() => fetchUpvoters(upvotersMeta.nextPage)}
                disabled={upvotersLoading}
                className='px-3 py-1.5 text-sm font-medium rounded-lg text-slate-900 dark:text-slate-100 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-50'
              >
                {upvotersLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </Modal>
      <ImageLightbox />
    </div>
  )
}

export default FeedbackShow
